import { useMemo, useCallback } from 'react';
import {
  KnownDeliveryContainerDtoStatus,
  KnownDeliveryContainerDtoType,
  KnownHubDetailsDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Button, Box, Typography } from '@mui/material';
import { DeliveryContainer } from '../../../lockers/models/delivery-container.model';
import { HubDetails } from '../../../lockers/models/hub-details.model';
import {
  HubCardWithTowers,
  HubCardWithTowersModeType,
  TransactionTypeHoverConfigMode
} from '../../../../ui/components/pure/virtual-hub/hub-card-with-towers/hub-card-with-towers.component';
import { ScreenOrientation } from '../../../../ui/components/pure/virtual-hub/kiosk-tile-content/kiosk-tile-content.component';
import {
  mapTowerDetailsToRenderData,
  mapSectionDetailsToRenderData
} from '../../../../ui/components/pure/virtual-hub/tower-renderer/tower-render-data.hub-details.mapper';
import {
  LockerRenderData,
  TowerRenderData
} from '../../../../ui/components/pure/virtual-hub/tower-renderer/tower-renderer.types';
import { SVGIconComponent } from '../../../../ui/components/pure/icons/types';
import { DeliveryContainerTileContent } from '../../../../ui/components/pure/virtual-hub/delivery-container-tile-content/delivery-container-tile-content.component';
import { KioskTileContent } from '../../../../ui/components/pure/virtual-hub/kiosk-tile-content/kiosk-tile-content.component';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { deliveryTypeToLabelMap } from '../../../deliveries/maps/delivery-type-to-label.map';
import { KnownDeliveryDtoDeliveryType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { AllocationPanel } from './allocation-panel.component';
import { EffectiveModules, effectiveModulesToDeliveryTypes } from './compute-effective-modules';
import { useHubAllocationsFormStyles } from './hub-allocations-form.styles';
import { Space } from '../../../../ui/components/pure/icons/Space.icon';
import { All } from '../../../../ui/components/pure/icons/All.icon';
import { Multiple } from '../../../../ui/components/pure/icons/Multiple.icon';

export interface HubAllocationsFormProps {
  hubDetails: HubDetails;
  selectedIds: Set<string>;
  transactionTypeProps: { value: string; icon: SVGIconComponent }[] | undefined;
  externalModules: string[];
  internalModules: string[];
  deliveryContainerMap: Map<string, DeliveryContainer>;
  effectiveModulesMap: Map<string, EffectiveModules>;
  acceptsTransactionType: (lockerId: string, type: string) => boolean;
  onToggleSelection: (id: string) => void;
  onToggleTowerSelection: (towerId: string) => void;
  onClearSelection: () => void;
  onSetPending: (module: string, type: 'internal' | 'external', action: 'add' | 'remove') => void;
  onSetAllPending: (action: 'add' | 'remove') => void;
}

export function HubAllocationsForm({
  hubDetails,
  selectedIds,
  externalModules,
  internalModules,
  transactionTypeProps,
  deliveryContainerMap,
  effectiveModulesMap,
  acceptsTransactionType,
  onToggleSelection,
  onToggleTowerSelection,
  onClearSelection,
  onSetPending,
  onSetAllPending
}: HubAllocationsFormProps) {
  const classes = useHubAllocationsFormStyles();
  const hasSelection = selectedIds.size > 0;

  const towers = useMemo((): TowerRenderData[] => {
    const towerData = (hubDetails.towers ?? []).map((tower) => {
      const data = mapTowerDetailsToRenderData(tower, hubDetails.type);
      data.lockers = data.lockers.map((locker) => {
        const container = deliveryContainerMap.get(locker.id);
        const isKiosk = !!container?.isKiosk;
        const isDisabled = container?.status === KnownDeliveryContainerDtoStatus.Disabled;
        return {
          ...locker,
          isHighlighted: selectedIds.has(locker.id),
          highlightColor: selectedIds.has(locker.id) ? '#1976d2' : locker.highlightColor,
          backgroundColor: selectedIds.has(locker.id) ? '#BBDEFB' : isKiosk || isDisabled ? '#E6E8EA' : '#FFFFFF'
        };
      });
      return data;
    });

    const sectionData = (hubDetails.sections ?? []).map((section) => {
      const data = mapSectionDetailsToRenderData(section);
      data.lockers = data.lockers.map((locker) => {
        const container = deliveryContainerMap.get(locker.id);
        const isKiosk = !!container?.isKiosk;
        const isDisabled = container?.status === KnownDeliveryContainerDtoStatus.Disabled;
        return {
          ...locker,
          isHighlighted: selectedIds.has(locker.id),
          highlightColor: selectedIds.has(locker.id) ? '#1976d2' : locker.highlightColor,
          backgroundColor: selectedIds.has(locker.id) ? '#BBDEFB' : isKiosk || isDisabled ? '#E6E8EA' : '#FFFFFF'
        };
      });
      return data;
    });

    return [...towerData, ...sectionData];
  }, [hubDetails, selectedIds, deliveryContainerMap]);

  const totalAvailableTypes = transactionTypeProps?.length ?? 0;

  const renderLockerContent = useCallback(
    (locker: LockerRenderData) => {
      const container = deliveryContainerMap.get(locker.id);
      const selectable = container && !container.isKiosk;

      const effective = effectiveModulesMap.get(locker.id);
      const accepted = effective ? effectiveModulesToDeliveryTypes(effective) : [];
      const rightIcon = pickRightIcon(accepted, totalAvailableTypes);
      const rightIconTooltip = buildRightIconTooltip(accepted, totalAvailableTypes);

      const isSpace = locker.sizeName === KnownDeliveryContainerDtoType.Space;
      const content = container?.isKiosk ? (
        <KioskTileContent
          screenOnly={isSpace}
          hubType={hubDetails.type}
          kioskScreenOrientation={
            hubDetails.type === KnownHubDetailsDtoType.LockersHubExternal
              ? ScreenOrientation.Landscape
              : ScreenOrientation.Portrait
          }
        />
      ) : isSpace ? (
        <DeliveryContainerTileContent
          deliveryContainerName={locker.name}
          Icon={Space}
          RightIcon={rightIcon}
          rightIconTooltip={rightIconTooltip}
        />
      ) : (
        <DeliveryContainerTileContent
          deliveryContainerName={locker.name}
          Icon={locker.sizeIcon}
          RightIcon={rightIcon}
          rightIconTooltip={rightIconTooltip}
        />
      );

      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (selectable) onToggleSelection(locker.id);
          }}
          style={{
            width: '100%',
            height: '100%',
            cursor: selectable ? 'pointer' : 'default',
            userSelect: 'none'
          }}
        >
          {content}
        </div>
      );
    },
    [deliveryContainerMap, onToggleSelection, hubDetails.type, effectiveModulesMap, totalAvailableTypes]
  );

  return (
    <Box
      sx={{
        pb: 2,
        mb: 2
      }}
    >
      <Box
        sx={{
          mb: 2,
          pl: '28px',
          pr: '24px'
        }}
      >
        <Typography variant="h6">Select lockers or whole towers, then use these actions to allocate</Typography>
      </Box>

      <Box className={classes.allocationPanelWrapper}>
        <AllocationPanel
          hasSelection={hasSelection}
          externalModules={externalModules}
          internalModules={internalModules}
          onSetPending={onSetPending}
          onSetAllPending={onSetAllPending}
        />
      </Box>

      <HubCardWithTowers
        hideHeader
        hideCardFrame
        headerProps={{
          hubName: hubDetails.name,
          hubStatus: hubDetails.status,
          statusIcon: hubDetails.statusIcon,
          statusColor: hubDetails.statusColor,
          deliveryContainerCount: hubDetails.deliveryContainersCount,
          deliveryContainerType: hubDetails.deliveryContainerType,
          deliveryContainerCountsBySize: hubDetails.deliveryContainerCountsSortedBySize,
          actions: [],
          isPreview: true
        }}
        towers={towers}
        maxModuleHeight={hubDetails.maxModuleHeight}
        isDoubleSided={hubDetails.isDoubleSided}
        sideSelectOptions={hubDetails.isDoubleSided ? ['outside', 'inside'] : []}
        onSelectSide={() => {}}
        mode={{ type: HubCardWithTowersModeType.Interactive, hideTooltip: true, renderLockerContent }}
        transactionTypes={transactionTypeProps}
        transactionTypeHover={{
          mode: TransactionTypeHoverConfigMode.Outline,
          accepts: acceptsTransactionType,
          color: '#34AEA7'
        }}
        transactionTypesRightSlot={
          <Box
            sx={{
              pl: 2
            }}
          >
            <Button size="small" variant="outlined" disabled={!hasSelection} onClick={onClearSelection}>
              Clear Selection
            </Button>
          </Box>
        }
        onTowerTitleClick={onToggleTowerSelection}
      />
    </Box>
  );
}

function pickRightIcon(accepted: KnownDeliveryDtoDeliveryType[], totalAvailable: number): SVGIconComponent | undefined {
  if (totalAvailable === 0) return undefined;
  if (accepted.length === 0 || accepted.length === totalAvailable) return All;
  if (accepted.length === 1) return deliveryTypeToIconMap[accepted[0]];
  return Multiple;
}

function buildRightIconTooltip(accepted: KnownDeliveryDtoDeliveryType[], totalAvailable: number): string | undefined {
  if (totalAvailable === 0) return undefined;
  if (accepted.length === 0 || accepted.length === totalAvailable) return 'All';
  return accepted.map((t) => deliveryTypeToLabelMap[t]).join(', ');
}
